import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { useCar } from '@/contexts/CarContext';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// Removed popover calendar for date; using native input styling like AddCostPage
import { Euro, Clock, Info, ChevronDown, ChevronUp, Plus, X, Check, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { LocationPicker } from '@/components/location/LocationPicker';
import type { PlaceData } from '@/types/location';
import { getRepairShopsForCar, saveRepairShopForCar, updateRepairShop, deleteRepairShop, type RepairShop } from '@/services/repairShops';
import { WorkItemAutocomplete } from '@/components/work/WorkItemAutocomplete';
import { REPAIR_JOBS, type RepairJob } from '@/data/repairJobs';

const SERVICE_TYPE_OPTIONS = ['maintenance', 'repair', 'upgrade', 'inspection'] as const;

type OpenTaskItem = {
  id: string;
  title: string;
  kind: 'standalone' | 'suggested';
  sourceServiceEntryId?: string;
  taskText?: string;
};

type ServiceTaskResolvedRef = {
  source_service_entry_id: string;
  task_text: string;
};

function makeSuggestedTaskId(serviceEntryId: string, taskText: string): string {
  return `svc:${serviceEntryId}:${encodeURIComponent(taskText)}`;
}

function parseSuggestedTaskId(id: string): ServiceTaskResolvedRef | null {
  if (!id.startsWith('svc:')) return null;
  const rest = id.slice(4);
  const sep = rest.indexOf(':');
  if (sep < 0) return null;
  try {
    return {
      source_service_entry_id: rest.slice(0, sep),
      task_text: decodeURIComponent(rest.slice(sep + 1)),
    };
  } catch {
    return null;
  }
}

function isStandaloneTaskId(id: string): boolean {
  return !id.startsWith('svc:');
}

// MVP local storage for saved repair shops (swappable to Supabase later)
type ServiceProvider = {
  id: string;
  name: string;
  location?: string;
  phone?: string;
  mechanic?: string;
  notes?: string;
  registrationNumber?: string;
  bankAccounts?: string[];
};
const KEY_PROVIDERS = 'ow_service_providers_v1';

function useServiceProviders() {
  const [providers, setProviders] = React.useState<ServiceProvider[]>([]);
  React.useEffect(() => {
    try { setProviders(JSON.parse(localStorage.getItem(KEY_PROVIDERS) || '[]')); } catch {}
  }, []);
  const saveAll = (list: ServiceProvider[]) => {
    setProviders(list);
    try { localStorage.setItem(KEY_PROVIDERS, JSON.stringify(list)); } catch {}
  };
  return {
    providers,
    add: (p: Omit<ServiceProvider, 'id'>) => saveAll([{ id: crypto.randomUUID(), ...p }, ...providers]),
    remove: (id: string) => saveAll(providers.filter(p => p.id !== id)),
  };
}

// Repair Shop Card Component
const RepairShopCard: React.FC<{ 
  shopName: string;
  mechanicName: string;
  location: string;
  contactPhone: string;
  registrationNumber?: string;
  bankAccounts?: string[];
  notes?: string;
  onClear: () => void;
  onEdit: () => void;
}> = ({ shopName, mechanicName, location, contactPhone, registrationNumber, bankAccounts, notes, onClear, onEdit }) => {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default
  
  return (
    <div className="w-full rounded-2xl bg-white shadow-md border border-gray-100 p-4">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-800">
          {t('addService.selectedRepairShop') || 'Selected Repair Shop'}
        </h3>
        <button
          type="button"
          onClick={onClear}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={t('actions.close') || 'Close'}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {/* Details */}
      <div className="space-y-2 text-xs">
        {shopName && (
          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-500 flex-shrink-0">{t('addService.shopNameLabel') || 'Name'}:</span>
            <span className="font-medium text-gray-800 text-right break-words">{shopName}</span>
          </div>
        )}
        {mechanicName && (
          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-500 flex-shrink-0">{t('addService.mechanicLabel') || 'Mechanic'}:</span>
            <span className="font-medium text-gray-800 text-right break-words">{mechanicName}</span>
          </div>
        )}
        {location && (
          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-500 flex-shrink-0">{t('addService.location') || 'Location'}:</span>
            <span className="font-medium text-gray-800 text-right break-words">{location}</span>
          </div>
        )}
        {contactPhone && (
          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-500 flex-shrink-0">{t('addService.phoneLabel') || 'Phone'}:</span>
            <span className="font-medium text-gray-800 text-right break-words">{contactPhone}</span>
          </div>
        )}
        
        {/* Expanded Details - Always visible, shows all fields even if empty */}
        {isExpanded && (
          <div className="pt-2 border-t border-gray-100 space-y-2">
            <div className="flex justify-between items-start gap-2">
              <span className="text-gray-500 flex-shrink-0">{t('addService.registrationNumber') || 'Registration number'}:</span>
              <span className="font-medium text-gray-800 text-right break-words">
                {registrationNumber || ''}
              </span>
            </div>
            <div className="flex justify-between items-start gap-2">
              <span className="text-gray-500 flex-shrink-0">{t('addService.bankAccounts') || 'Bank accounts'}:</span>
              <div className="text-right flex-1">
                {bankAccounts && bankAccounts.length > 0 && bankAccounts.filter(Boolean).length > 0 ? (
                  bankAccounts.filter(Boolean).map((acc, idx) => (
                    <div key={idx} className="font-medium text-gray-800 break-words">
                      {acc}
                    </div>
                  ))
                ) : (
                  ''
                )}
              </div>
            </div>
            <div className="flex justify-between items-start gap-2">
              <span className="text-gray-500 flex-shrink-0">{t('labels.notes') || 'Notes'}:</span>
              <span className="font-medium text-gray-800 text-right break-words whitespace-pre-wrap">
                {notes || ''}
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Action Buttons at Bottom */}
      <div className="flex items-center justify-between gap-3 pt-3 mt-3 border-t border-gray-100">
        <button
          type="button"
          onClick={onEdit}
          className="text-xs px-4 py-2 rounded-lg text-gray-700 font-medium transition-colors hover:text-gray-900"
        >
          {t('actions.edit') || 'Edit'}
        </button>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs px-4 py-2 rounded-lg bg-[#F2F2F2] hover:bg-[#F2F2F2]/90 text-gray-900 transition-colors"
        >
          {isExpanded ? (t('common.hideDetails') || 'Hide Details') : (t('common.viewDetails') || 'View Details')}
        </button>
      </div>
    </div>
  );
};

// Accordion section: controlled open state so only one section is open at a time
const Section: React.FC<{ title: string; children: React.ReactNode; isOpen: boolean; onToggle: () => void; rightNode?: React.ReactNode }> = ({ title, children, isOpen, onToggle, rightNode }) => {
  return (
    <section className="rounded-2xl bg-white shadow-md border border-gray-100">
      <button type="button" onClick={onToggle} aria-expanded={isOpen} className="w-full flex justify-between items-center px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50 rounded-t-2xl">
        <div className="flex items-center gap-2">{title}</div>
        <div className="flex items-center gap-3">
          {rightNode}
          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>
      <div
        className={`border-t ${isOpen ? 'border-gray-100' : 'border-transparent'} overflow-hidden transition-all duration-300 ease-out ${
          isOpen ? 'max-h-[2000px] opacity-100 py-4' : 'max-h-0 opacity-0 py-0'
        }`}
      >
        <div className="space-y-3 px-4">
          {children}
        </div>
      </div>
    </section>
  );
};

const AddServiceEntryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cars } = useCar();
  const { currentCar } = useCarSelectionContext();
  const { toast } = useToast();
  const { formatCurrency, t, language } = useI18n();
  
  // Form state
  const [selectedCarId, setSelectedCarId] = useState<string>('');
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]); // Multi-select: repair, maintenance, upgrade
  const [cost, setCost] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [serviceDate, setServiceDate] = useState<Date>(new Date());
  const [odometerReading, setOdometerReading] = useState<string>('');
  const [lastOdometerReading, setLastOdometerReading] = useState<number | null>(null);
  const [mechanicName, setMechanicName] = useState<string>('');
  const [shopName, setShopName] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [contactPhone, setContactPhone] = useState<string>('');
  const [openIssues, setOpenIssues] = useState<Array<{ id: string; title: string }>>([]);
  const [resolvedIssueIds, setResolvedIssueIds] = useState<string[]>([]);
  const [noneSelected, setNoneSelected] = useState<boolean>(false);
  const [openTasks, setOpenTasks] = useState<OpenTaskItem[]>([]);
  const [resolvedTaskIds, setResolvedTaskIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [showOdometerInfo, setShowOdometerInfo] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<Array<{ path: string; url: string }>>([]);
  const QUICK_ACTIONS = [
    'Akumulators','Bremžu uzlikas','Bremžu šķidrums','Degvielas filtrs','Eļļas filtrs','Eļļas maiņa','Gaisa filtrs','Gaisa kondicionētājs','Gaismas','Piekare','Pārbaude','Jaunas riepas','Riepu rotācija','Riepu spiediens','Riteņu savirze','Transmisijas eļļa','Antifreeze','Sveces'
  ];
  const [workDone, setWorkDone] = useState<Array<{ 
    title: string; 
    notes?: string; 
    spec_code?: string; 
    label_photo?: string; 
    color?: string;
  }>>([{ title: '' }]);
  const [finding, setFinding] = useState<string>('');
  const [recommendation, setRecommendation] = useState<string>('');
  const [fixes, setFixes] = useState<Array<{ fix: string; due: string }>>([{ fix: '', due: '' }]);
  const [fixDrawerOpen, setFixDrawerOpen] = useState(false);
  const [intervalDrawerOpen, setIntervalDrawerOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [mode, setMode] = useState<'km' | 'time'>('km');
  const [km, setKm] = useState<string>('');
  const [time, setTime] = useState<string>('');
  // Saved providers
  const { providers, add: addProvider, remove: removeProvider } = useServiceProviders();
  const [providerPickerOpen, setProviderPickerOpen] = useState(false);
  const [providerAddOpen, setProviderAddOpen] = useState(false);
  const [providerQuery, setProviderQuery] = useState('');
  const [newShopName, setNewShopName] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newMechanicName, setNewMechanicName] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [providerMoreOpen, setProviderMoreOpen] = useState(false);
  const [newRegistration, setNewRegistration] = useState('');
  const [newBankAccounts, setNewBankAccounts] = useState<string[]>(['']);
  const [providerLocationPickerOpen, setProviderLocationPickerOpen] = useState(false);
  const [providerSelected, setProviderSelected] = useState(false);
  const [savedShops, setSavedShops] = useState<RepairShop[]>([]);
  const [selectedSavedShopId, setSelectedSavedShopId] = useState<string>('');
  const [editingShopId, setEditingShopId] = useState<string | null>(null);
  const [deleteConfirmShopId, setDeleteConfirmShopId] = useState<string | null>(null);
  const [workNoteDrawerOpen, setWorkNoteDrawerOpen] = useState(false);
  const [editingWorkNoteIndex, setEditingWorkNoteIndex] = useState<number | null>(null);
  const [labelPhotoFile, setLabelPhotoFile] = useState<File | null>(null);
  const [labelPhotoPreview, setLabelPhotoPreview] = useState<string | null>(null);
  const labelPhotoInputRef = React.useRef<HTMLInputElement>(null);
  const [isRemoveMode, setIsRemoveMode] = useState(false);
  const [isRemoveFixMode, setIsRemoveFixMode] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  
  // Place data from LocationPicker (Phase 1 - no DB writes yet)
  const [placeData, setPlaceData] = useState<PlaceData | null>(null);
  const [loadedEntryData, setLoadedEntryData] = useState<any>(null);

  // Get car from URL params or current selection
  const carIdFromParams = searchParams.get('carId');
  const editEntryId = searchParams.get('edit');
  const requestId = searchParams.get('requestId');
  const defaultCarId = carIdFromParams || currentCar?.id || '';
  
  // Service request state
  const [serviceRequest, setServiceRequest] = useState<{
    id: string;
    title?: string;
    owner_notes: string;
    car_id: string;
  } | null>(null);

  useEffect(() => {
    if (defaultCarId && cars.find(c => c.id === defaultCarId)) {
      setSelectedCarId(defaultCarId);
    }
  }, [defaultCarId, cars]);

  // Load service request if requestId is provided
  useEffect(() => {
    if (!requestId) {
      setServiceRequest(null);
      return;
    }

    const loadServiceRequest = async () => {
      try {
        const { data, error } = await supabase
          .from('service_requests')
          .select('id, title, owner_notes, car_id')
          .eq('id', requestId)
          .single();

        if (error) {
          console.error('Error loading service request:', error);
          setServiceRequest(null);
        } else {
          setServiceRequest(data);
          // Set car ID from service request if not already set
          if (data.car_id && !selectedCarId) {
            setSelectedCarId(data.car_id);
          }
        }
      } catch (error) {
        console.error('Error loading service request:', error);
        setServiceRequest(null);
      }
    };

    loadServiceRequest();
  }, [requestId, selectedCarId]);

  // Load service entry for editing
  useEffect(() => {
    if (!editEntryId) return;

    const loadServiceEntry = async () => {
      try {
        const { data, error } = await supabase
          .from('service_entries')
          .select('*')
          .eq('id', editEntryId)
          .single();

        if (error) {
          console.error('Failed to load service entry:', error);
          toast({
            title: t('common.error') || 'Error',
            description: t('common.failedToLoad') || 'Failed to load service entry',
            variant: 'destructive',
          });
          return;
        }

        if (data) {
          setLoadedEntryData(data);
          
          // Set car ID
          if (data.car_id) {
            setSelectedCarId(data.car_id);
          }

          // Set basic fields
          if (data.service_date) {
            setServiceDate(new Date(data.service_date));
          }
          if (data.odometer_km) {
            setOdometerReading(data.odometer_km.toString());
          }
          if (data.mechanic_name) {
            setMechanicName(data.mechanic_name);
          }
          if (data.shop_name) {
            setShopName(data.shop_name);
          }
          if (data.location) {
            setLocation(data.location);
          }
          if (data.contact_phone) {
            setContactPhone(data.contact_phone);
          }
          if (data.total_cents) {
            setCost((data.total_cents / 100).toString());
          }
          // Restore service types
          if (Array.isArray(data.service_types) && data.service_types.length > 0) {
            setSelectedServiceTypes(data.service_types as string[]);
          } else {
            setSelectedServiceTypes([]);
          }
          if (data.findings) {
            setFinding(data.findings);
          }
          if (data.recommendations) {
            setRecommendation(data.recommendations);
          }
          if (data.suggested_next_fixes && Array.isArray(data.suggested_next_fixes)) {
            setFixes(data.suggested_next_fixes.map((r: any) => ({
              fix: r.fix || '',
              due: r.due || '',
            })));
          }
          if (data.work_done && Array.isArray(data.work_done) && data.work_done.length > 0) {
            setWorkDone(data.work_done.map((w: any) => ({
              title: w.title || '',
              notes: w.notes || '',
              spec_code: w.spec_code || '',
              label_photo: w.label_photo || '',
              color: w.color || '',
            })));
          }
          // Handle resolved issues
          if (Array.isArray(data.issues_resolved_ids) && data.issues_resolved_ids.length > 0) {
            // There are specific resolved issues
            setNoneSelected(false);
            setResolvedIssueIds(data.issues_resolved_ids);

            // Load resolved issues for display (they may no longer be "open")
            if (data.car_id) {
              try {
                const { data: resolvedData, error: resolvedError } = await supabase
                  .from('symptoms')
                  .select('id,title,status')
                  .eq('car_id', data.car_id)
                  .in('id', data.issues_resolved_ids);
                
                if (!resolvedError && resolvedData) {
                  // Add resolved issues to openIssues if not already there
                  setOpenIssues((prev) => {
                    const existingIds = new Set(prev.map(iss => iss.id));
                    const newResolved = (resolvedData || [])
                      .filter((s: any) => !existingIds.has(s.id))
                      .map((s: any) => ({ id: s.id, title: s.title || 'Issue' }));
                    return [...prev, ...newResolved];
                  });
                }
              } catch (e) {
                console.warn('Failed to load resolved issues for display:', e);
              }
            }
          } else {
            // No issue IDs; rely on the boolean flag
            if (data.no_issues_resolved) {
              setNoneSelected(true);
              setResolvedIssueIds([]);
            } else {
              setNoneSelected(false);
              setResolvedIssueIds([]);
            }
          }
          // Handle resolved tasks (standalone + suggested next-fixes)
          const restoredTaskIds: string[] = [];
          const restoredTaskItems: OpenTaskItem[] = [];

          if (Array.isArray(data.tasks_resolved_ids) && data.tasks_resolved_ids.length > 0) {
            restoredTaskIds.push(...data.tasks_resolved_ids);
            if (data.car_id) {
              try {
                const { data: resolvedTasksData, error: resolvedTasksError } = await supabase
                  .from('tasks')
                  .select('id,title,status')
                  .eq('car_id', data.car_id)
                  .in('id', data.tasks_resolved_ids);

                if (!resolvedTasksError && resolvedTasksData) {
                  for (const task of resolvedTasksData) {
                    restoredTaskItems.push({
                      id: task.id,
                      title: task.title || 'Task',
                      kind: 'standalone',
                    });
                  }
                }
              } catch (e) {
                console.warn('Failed to load resolved tasks for display:', e);
              }
            }
          }

          if (Array.isArray(data.service_tasks_resolved) && data.service_tasks_resolved.length > 0) {
            for (const ref of data.service_tasks_resolved as ServiceTaskResolvedRef[]) {
              if (!ref?.source_service_entry_id || !ref?.task_text) continue;
              const id = makeSuggestedTaskId(ref.source_service_entry_id, ref.task_text);
              restoredTaskIds.push(id);
              restoredTaskItems.push({
                id,
                title: ref.task_text,
                kind: 'suggested',
                sourceServiceEntryId: ref.source_service_entry_id,
                taskText: ref.task_text,
              });
            }
          }

          setResolvedTaskIds(restoredTaskIds);
          if (restoredTaskItems.length > 0) {
            setOpenTasks((prev) => {
              const existingIds = new Set(prev.map((task) => task.id));
              const extras = restoredTaskItems.filter((task) => !existingIds.has(task.id));
              return [...prev, ...extras];
            });
          }
          if (data.place_id || data.lat || data.lng) {
            setPlaceData({
              place_id: data.place_id || undefined,
              lat: data.lat || undefined,
              lng: data.lng || undefined,
              formatted_address: data.location || undefined,
            });
          }
          if (data.shop_name || data.mechanic_name || data.location || data.contact_phone) {
            setProviderSelected(true);
          }
          // Load existing images
          if (data.media && Array.isArray(data.media) && data.media.length > 0) {
            const { getSignedViewUrl } = await import('@/lib/getSignedViewUrl');
            const imagePromises = data.media
              .filter((m: any) => m.path && m.type === 'image')
              .map(async (m: any) => {
                try {
                  const signedUrlInfo = await getSignedViewUrl(m.path);
                  return { path: m.path, url: signedUrlInfo.url };
                } catch (e) {
                  console.warn('Failed to get signed URL for', m.path, e);
                  return null;
                }
              });
            const loadedImages = (await Promise.all(imagePromises)).filter(Boolean) as Array<{ path: string; url: string }>;
            setExistingImages(loadedImages);
          }
        }
      } catch (error) {
        console.error('Error loading service entry:', error);
        toast({
          title: t('common.error') || 'Error',
          description: t('common.failedToLoad') || 'Failed to load service entry',
          variant: 'destructive',
        });
      }
    };

    loadServiceEntry();
  }, [editEntryId, toast, t]);

  // Fetch last odometer reading when car changes
  useEffect(() => {
    if (selectedCarId) {
      const fetchLastOdometer = async () => {
        try {
          const { data } = await supabase
            .from('odometer_readings')
            .select('reading, recorded_at')
            .eq('car_id', selectedCarId)
            // Latest reading = highest reading value (regardless of date); if equal, newest date wins
            .order('reading', { ascending: false })
            .order('recorded_at', { ascending: false })
            .limit(10); // Fetch top 10 to check for outliers
          
          if (data && data.length > 0) {
            const { filterOdometerOutliers } = await import('@/lib/utils');
            const validReading = filterOdometerOutliers(data);
            setLastOdometerReading(validReading);
          }
        } catch (error) {
          console.error('Error fetching last odometer reading:', error);
        }
      };
      
      fetchLastOdometer();
    }
  }, [selectedCarId]);

  // Load open issues (symptoms) for the car
  useEffect(() => {
    if (!selectedCarId) return;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('symptoms')
          .select('id,title,status')
          .eq('car_id', selectedCarId)
          .eq('status', 'open');
        if (error) throw error;
        setOpenIssues((data || []).map((s: any) => ({ id: s.id, title: s.title || 'Issue' })));
      } catch (e) {
        console.warn('Failed to load open issues:', e);
      }
    })();
  }, [selectedCarId]);

  // Load open tasks: standalone pending/scheduled + unfinished suggested next-fixes
  useEffect(() => {
    if (!selectedCarId) {
      setOpenTasks([]);
      return;
    }
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        const [{ data: standaloneData, error: standaloneError }, { data: entriesData, error: entriesError }, { data: hiddenData, error: hiddenError }] =
          await Promise.all([
            supabase
              .from('tasks')
              .select('id,title,status')
              .eq('car_id', selectedCarId)
              .in('status', ['pending', 'scheduled']),
            supabase
              .from('service_entries')
              .select('id, suggested_next_fixes')
              .eq('car_id', selectedCarId)
              .not('suggested_next_fixes', 'is', null),
            user?.id
              ? supabase
                  .from('hidden_service_tasks')
                  .select('service_entry_id, task_text, hidden, status')
                  .eq('user_id', user.id)
                  .eq('car_id', selectedCarId)
              : Promise.resolve({ data: [], error: null }),
          ]);

        if (standaloneError) throw standaloneError;
        if (entriesError) console.warn('Failed to load suggested fixes for tasks:', entriesError);
        if (hiddenError) console.warn('Failed to load hidden service tasks:', hiddenError);

        const hiddenMap = new Map<string, { hidden: boolean; status?: string }>();
        (hiddenData || []).forEach((row: any) => {
          const raw = String(row.task_text ?? '');
          const trimmed = raw.trim();
          const overlay = { hidden: !!row.hidden, status: row.status };
          hiddenMap.set(`${row.service_entry_id}:${raw}`, overlay);
          if (trimmed && trimmed !== raw) {
            hiddenMap.set(`${row.service_entry_id}:${trimmed}`, overlay);
          }
        });

        const loaded: OpenTaskItem[] = (standaloneData || []).map((task: any) => ({
          id: task.id,
          title: task.title || 'Task',
          kind: 'standalone' as const,
        }));

        for (const entry of entriesData || []) {
          // When editing, skip suggested fixes that belong to this same entry
          if (editEntryId && entry.id === editEntryId) continue;
          const fixes = entry.suggested_next_fixes || [];
          if (!Array.isArray(fixes)) continue;
          for (const rec of fixes) {
            const taskText =
              typeof rec === 'string'
                ? rec.trim()
                : typeof rec?.fix === 'string'
                  ? rec.fix.trim()
                  : '';
            if (!taskText) continue;
            const hiddenInfo = hiddenMap.get(`${entry.id}:${taskText}`);
            if (hiddenInfo?.hidden) continue;
            const status = hiddenInfo?.status || 'open';
            if (status === 'done' || status === 'completed') continue;

            loaded.push({
              id: makeSuggestedTaskId(entry.id, taskText),
              title: taskText,
              kind: 'suggested',
              sourceServiceEntryId: entry.id,
              taskText,
            });
          }
        }

        setOpenTasks((prev) => {
          const loadedIds = new Set(loaded.map((task) => task.id));
          const extras = prev.filter((task) => !loadedIds.has(task.id));
          return [...loaded, ...extras];
        });
      } catch (e) {
        console.warn('Failed to load open tasks:', e);
      }
    })();
  }, [selectedCarId, editEntryId]);

  useEffect(() => {
    if (!selectedCarId) {
      setSavedShops([]);
      return;
    }
    (async () => {
      try {
        const shops = await getRepairShopsForCar(selectedCarId);
        setSavedShops(shops);
      } catch (e) {
        console.warn('Failed to load saved repair shops', e);
      }
    })();
  }, [selectedCarId]);

  // Only show shops from Supabase (database) - no localStorage duplicates
  const pickerList = React.useMemo(() => {
    if (!providerQuery) return savedShops;
    return savedShops.filter(p => 
      ((p.name || '') + ' ' + (p.formatted_address || '')).toLowerCase().includes(providerQuery.toLowerCase())
    );
  }, [savedShops, providerQuery]);

  // Toggle service type selection
  const toggleServiceType = (type: string) => {
    setSelectedServiceTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  // UI helpers matching provided design
  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const totalPhotos = files.length + existingImages.length;
    const remainingSlots = Math.max(0, 5 - totalPhotos);
    if (remainingSlots > 0) {
      setFiles((prev) => [...prev, ...newFiles].slice(0, remainingSlots));
    }
  };

  const handleFileRemove = (i: number) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  
  const handleExistingImageRemove = (path: string) => {
    setExistingImages((prev) => prev.filter((img) => img.path !== path));
  };

  const handleWorkDoneTitle = (i: number, val: string) => {
    setWorkDone((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], title: val };
      return copy;
    });
  };

  const handleWorkDoneNotes = (i: number, val: string) => {
    setWorkDone((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], notes: val };
      return copy;
    });
  };

  const handleWorkDoneSpecCode = (i: number, val: string) => {
    setWorkDone((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], spec_code: val };
      return copy;
    });
  };

  const handleWorkDoneLabelPhoto = (i: number, val: string) => {
    setWorkDone((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], label_photo: val };
      return copy;
    });
  };

  const handleWorkDoneColor = (i: number, val: string) => {
    setWorkDone((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], color: val };
      return copy;
    });
  };

  // Check if a work item requires fluid details
  const requiresFluidDetails = (title: string): boolean => {
    const fluidWorkItems = [
      'engine_oil_change',
      'engine_oil_refill',
      'coolant_change',
      'coolant_refill',
      'brake_fluid_change',
      'brake_fluid_refill',
    ];
    const job = REPAIR_JOBS.find(j => 
      j.en.toLowerCase() === title.toLowerCase() || 
      j.lv.toLowerCase() === title.toLowerCase()
    );
    return job ? fluidWorkItems.includes(job.id) : false;
  };

  // Check if work item is coolant-related (needs color field)
  const isCoolantWork = (title: string): boolean => {
    const coolantWorkItems = ['coolant_change', 'coolant_refill'];
    const job = REPAIR_JOBS.find(j => 
      j.en.toLowerCase() === title.toLowerCase() || 
      j.lv.toLowerCase() === title.toLowerCase()
    );
    return job ? coolantWorkItems.includes(job.id) : false;
  };

  const handleWorkItemSelect = (index: number, job: RepairJob) => {
    const title = job[language as 'en' | 'lv'];
    handleWorkDoneTitle(index, title);
    
    // Auto-open notes drawer if this is a fluid-related work item
    if (requiresFluidDetails(title)) {
      setEditingWorkNoteIndex(index);
      setWorkNoteDrawerOpen(true);
    }
  };

  const addWorkField = () => setWorkDone((prev) => [...prev, { title: '' }]);
  
  const removeWorkField = (index: number) => {
    setWorkDone((prev) => {
      if (prev.length <= 1) {
        // Keep at least one empty field
        return [{ title: '' }];
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // Reset remove mode when there are less than 2 work items
  useEffect(() => {
    if (workDone.length < 2 && isRemoveMode) {
      setIsRemoveMode(false);
    }
  }, [workDone.length, isRemoveMode]);

  // Reset remove fix mode when there are less than 2 fixes
  useEffect(() => {
    if (fixes.length < 2 && isRemoveFixMode) {
      setIsRemoveFixMode(false);
    }
  }, [fixes.length, isRemoveFixMode]);

  // Initialize label photo preview when drawer opens
  useEffect(() => {
    if (workNoteDrawerOpen && editingWorkNoteIndex !== null) {
      const currentItem = workDone[editingWorkNoteIndex];
      if (currentItem?.label_photo && !labelPhotoPreview) {
        setLabelPhotoPreview(currentItem.label_photo);
      } else if (!currentItem?.label_photo) {
        setLabelPhotoPreview(null);
        setLabelPhotoFile(null);
      }
    } else {
      // Clear when drawer closes
      setLabelPhotoPreview(null);
      setLabelPhotoFile(null);
    }
  }, [workNoteDrawerOpen, editingWorkNoteIndex, workDone]);
  
  const clearWorkField = (index: number) => {
    handleWorkDoneTitle(index, '');
  };

  const addFixRow = () => setFixes((prev) => [...prev, { fix: '', due: '' }]);
  const removeFixRow = (i: number) => {
    setFixes((prev) => {
      const copy = [...prev];
      if (i === 0 && (copy[0].fix || copy[0].due)) copy[0] = { fix: '', due: '' };
      else copy.splice(i, 1);
      return copy.length ? copy : [{ fix: '', due: '' }];
    });
  };

  const openFixDrawer = (i: number) => { setActiveIndex(i); setFixDrawerOpen(true); };
  const openIntervalDrawer = (i: number) => {
    setActiveIndex(i);
    const currentDue = fixes[i]?.due || '';
    // Parse existing due value to set mode and values
    if (currentDue.includes('km')) {
      setMode('km');
      const kmValue = currentDue.replace(/\s*km\s*/g, '');
      setKm(kmValue);
      setTime('');
    } else if (currentDue) {
      setMode('time');
      setTime(currentDue);
      setKm('');
    } else {
      setMode('km');
      setKm('');
      setTime('');
    }
    setIntervalDrawerOpen(true);
  };

  // Handler for using a shop from the picker
  const handleUseShop = (shop: RepairShop | any) => {
    const normalizedPhone = Array.isArray(shop.phones) && shop.phones.length 
      ? shop.phones.map((ph: string) => ph.replace(/\s+/g, '')).join(', ')
      : '';
    
    setShopName(shop.name || '');
    setLocation(shop.formatted_address || '');
    setContactPhone(normalizedPhone);
    setMechanicName(shop.mechanic_name || '');
    
    // Populate additional fields for the card
    setNewRegistration(shop.registration_number || '');
    setNewBankAccounts(shop.bank_accounts && shop.bank_accounts.length > 0 ? shop.bank_accounts : ['']);
    setNewNotes(shop.notes || '');
    
    // Set the selected shop ID so we can edit it later
    setSelectedSavedShopId(shop.id || '');
    
    setProviderSelected(true);
    setProviderPickerOpen(false);
    
    // Set place data if available
    if (shop.place_id || (shop.lat && shop.lng)) {
      setPlaceData({
        place_id: shop.place_id || undefined,
        name: shop.name,
        formatted_address: shop.formatted_address || '',
        phones: Array.isArray(shop.phones) ? shop.phones : [],
        website: shop.website || undefined,
        lat: shop.lat!,
        lng: shop.lng!,
      });
    }
  };

  // Handler for editing a shop
  const handleEditShop = (shop: RepairShop | any) => {
    const normalizedPhone = Array.isArray(shop.phones) && shop.phones.length 
      ? shop.phones.map((ph: string) => ph.replace(/\s+/g, '')).join(', ')
      : '';
    
    setNewShopName(shop.name || '');
    setNewLocation(shop.formatted_address || '');
    setNewPhone(normalizedPhone);
    setNewMechanicName(shop.mechanic_name || '');
    setNewNotes(shop.notes || '');
    setNewRegistration(shop.registration_number || '');
    setNewBankAccounts(shop.bank_accounts && shop.bank_accounts.length > 0 ? shop.bank_accounts : ['']);
    setEditingShopId(shop.id);
    setProviderPickerOpen(false);
    setProviderAddOpen(true);
  };

  // Handler for deleting a shop
  const handleDeleteShop = async (shopId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) return;

      // Check if it's from Supabase or localStorage
      const isFromSupabase = savedShops.some(s => s.id === shopId);
      
      if (isFromSupabase) {
        // Delete from Supabase
        await deleteRepairShop(shopId);
        // Refresh list
        if (selectedCarId) {
          const shops = await getRepairShopsForCar(selectedCarId);
          setSavedShops(shops);
        }
      } else {
        // Delete from localStorage
        removeProvider(shopId);
      }
      
      setDeleteConfirmShopId(null);
      toast({
        title: t('actions.delete') || 'Deleted',
        description: t('common.deleted') || 'Repair shop deleted successfully.',
      });
    } catch (e: any) {
      console.error('Failed to delete repair shop:', e);
      toast({
        title: t('common.error') || 'Error',
        description: t('common.deleteError') || 'Failed to delete repair shop.',
        variant: 'destructive',
      });
    }
  };
  const handleFixAddDirect = (text: string) => {
    if (activeIndex == null) return;
    setFixes((prev) => { const copy = [...prev]; copy[activeIndex].fix = text; return copy; });
  };
  const confirmFixAdd = () => { setFixDrawerOpen(false); };
  const handleIntervalAdd = () => {
    if (activeIndex == null) return;
    const value = mode === 'km' ? (km ? `${km} km` : '') : time;
    setFixes((prev) => { const copy = [...prev]; copy[activeIndex].due = value; return copy; });
    setIntervalDrawerOpen(false);
  };

  // Sync fluid data from service entries to fluids table
  const syncFluidDataToFluidsTable = async (
    workDoneItems: Array<{ title: string; spec_code?: string; label_photo?: string; color?: string }>,
    serviceDate: Date,
    odometerReading: string | null,
    carId: string,
    userId: string,
    entryId: string
  ) => {
    // Mapping: repair job ID → { fluid_type, action_type }
    const fluidWorkItemMap: Record<string, { fluid_type: string; action_type: 'refill' | 'replacement' }> = {
      'engine_oil_change': { fluid_type: 'engine_oil', action_type: 'replacement' },
      'engine_oil_refill': { fluid_type: 'engine_oil', action_type: 'refill' },
      'coolant_change': { fluid_type: 'coolant', action_type: 'replacement' },
      'coolant_refill': { fluid_type: 'coolant', action_type: 'refill' },
      'brake_fluid_change': { fluid_type: 'brake_fluid', action_type: 'replacement' },
      'brake_fluid_refill': { fluid_type: 'brake_fluid', action_type: 'refill' },
    };

    // Find the repair job ID from the title
    const findRepairJobId = (title: string): string | null => {
      const job = REPAIR_JOBS.find(j => 
        j.en.toLowerCase() === title.toLowerCase() || 
        j.lv.toLowerCase() === title.toLowerCase()
      );
      return job?.id || null;
    };

    // Process each work item
    for (const item of workDoneItems) {
      if (!item.title?.trim()) continue;

      const jobId = findRepairJobId(item.title);
      if (!jobId || !fluidWorkItemMap[jobId]) continue;

      const { fluid_type, action_type } = fluidWorkItemMap[jobId];
      
      // Only sync if we have at least spec_code or label_photo
      if (!item.spec_code?.trim() && !item.label_photo?.trim()) continue;

      try {
        // Insert fluid record
        const { data: fluidRecord, error: fluidError } = await supabase
          .from('fluids')
          .insert({
            car_id: carId,
            user_id: userId,
            fluid_type,
            action_type,
            spec_code: item.spec_code?.trim() || null,
            color: item.color?.trim() || null,
            media_path: item.label_photo?.trim() || null,
            action_date: serviceDate.toISOString(),
            notes: `Synced from service entry: ${item.title}`,
          })
          .select('id')
          .single();

        if (fluidError) {
          console.error(`Failed to sync fluid ${fluid_type} ${action_type}:`, fluidError);
          continue;
        }

        // Create odometer reading linked to the fluid record (if odometer reading exists)
        if (odometerReading && fluidRecord?.id) {
          const readingValue = parseInt(odometerReading, 10);
          if (!Number.isNaN(readingValue)) {
            await supabase.from('odometer_readings').insert({
              car_id: carId,
              entered_by: userId,
              reading: readingValue,
              recorded_at: serviceDate.toISOString(),
              source: 'fluid_log',
              related_id: fluidRecord.id, // Link to fluid record, not service entry
            });
          }
        }
      } catch (error) {
        console.error(`Error syncing fluid data for ${item.title}:`, error);
        // Continue with other items even if one fails
      }
    }
  };

  // Section moved to top-level to preserve open state across re-renders

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCarId) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Handle both . and , as decimal separators; allow empty cost (stored as null)
      let totalCents: number | null = null;
      if (cost) {
        const normalizedCost = cost.replace(',', '.');
        const parsed = parseFloat(normalizedCost);
        if (Number.isNaN(parsed) || parsed < 0) {
          setError(t('addService.error') || 'Failed to add service entry. Please try again.');
          setSubmitting(false);
          return;
        }
        totalCents = Math.round(parsed * 100);
      }
      
      let entryId: string;
      
      if (editEntryId) {
        // Update existing entry
        const { error: updateError } = await supabase
          .from('service_entries')
          .update({
            car_id: selectedCarId,
            service_date: serviceDate.toISOString(),
            shop_name: shopName || null,
            mechanic_name: mechanicName || null,
            location: location || null,
            contact_phone: contactPhone || null,
            findings: finding || null,
            recommendations: recommendation || null,
            work_done: workDone.filter(w => w.title?.trim()),
            suggested_next_fixes: fixes.filter(f => f.fix?.trim() || f.due?.trim()),
            total_cents: totalCents,
            status: 'submitted',
            place_id: placeData?.place_id || null,
            lat: placeData?.lat || null,
            lng: placeData?.lng || null,
            service_types: selectedServiceTypes,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editEntryId);

        if (updateError) {
          throw updateError;
        }
        
        entryId = editEntryId;
      } else {
        // Create new entry
        const { data: inserted, error: insertError } = await supabase
          .from('service_entries')
          .insert({
            car_id: selectedCarId,
            user_id: user.id,
            service_date: serviceDate.toISOString(),
            shop_name: shopName || null,
            mechanic_name: mechanicName || null,
            location: location || null,
            contact_phone: contactPhone || null,
            findings: finding || null,
            recommendations: recommendation || null,
            work_done: workDone.filter(w => w.title?.trim()),
            suggested_next_fixes: fixes.filter(f => f.fix?.trim() || f.due?.trim()),
            total_cents: totalCents,
            status: 'submitted',
            place_id: placeData?.place_id || null,
            lat: placeData?.lat || null,
            lng: placeData?.lng || null,
            service_types: selectedServiceTypes,
          })
          .select('id')
          .single();

        if (insertError) {
          throw insertError;
        }

        entryId = inserted.id as string;
      }

      // 2) odometer reading source-of-truth (only for new entries, not when editing)
      if (odometerReading && !editEntryId) {
        await supabase.from('odometer_readings').insert({
          car_id: selectedCarId,
          entered_by: user.id,
          reading: parseInt(odometerReading),
          recorded_at: serviceDate.toISOString(),
          source: 'service',
          related_id: entryId,
        });
      }

      // 2.5) Sync fluid data to fluids table
      await syncFluidDataToFluidsTable(
        workDone.filter(w => w.title?.trim()),
        serviceDate,
        odometerReading,
        selectedCarId,
        user.id,
        entryId
      );

      // Note: Service costs are now read directly from service_entries table
      // No need to create expense records anymore

      // 3) resolve selected issues (symptoms) that are open
      if (noneSelected) {
        // Explicitly say: user chose "None of the recorded issues"
        await supabase
          .from('service_entries')
          .update({
            issues_resolved_ids: [],
            no_issues_resolved: true,
          })
          .eq('id', entryId);
      } else if (resolvedIssueIds.length > 0) {
        await supabase
          .from('symptoms')
          .update({ status: 'resolved', resolved_at: new Date().toISOString() })
          .in('id', resolvedIssueIds)
          .eq('status', 'open');
        await supabase
          .from('service_entries')
          .update({
            issues_resolved_ids: resolvedIssueIds,
            no_issues_resolved: false,
          })
          .eq('id', entryId);
      } else {
        // Clear resolved issues if nothing selected
        await supabase
          .from('service_entries')
          .update({
            issues_resolved_ids: [],
            no_issues_resolved: false,
          })
          .eq('id', entryId);
      }

      // 3.5) resolve selected tasks (standalone + suggested next-fixes)
      const standaloneResolvedIds = resolvedTaskIds.filter(isStandaloneTaskId);
      const suggestedResolvedRefs = resolvedTaskIds
        .map(parseSuggestedTaskId)
        .filter((ref): ref is ServiceTaskResolvedRef => !!ref);

      if (standaloneResolvedIds.length > 0) {
        const nowIso = new Date().toISOString();
        const { data: tasksToUpdate, error: tasksFetchError } = await supabase
          .from('tasks')
          .select('id, service_entry_id, status')
          .in('id', standaloneResolvedIds);

        if (tasksFetchError) throw tasksFetchError;

        for (const task of tasksToUpdate || []) {
          const updates: Record<string, unknown> = {
            updated_at: nowIso,
          };
          if (task.status === 'pending' || task.status === 'scheduled') {
            updates.status = 'done';
            updates.completed_at = nowIso;
          }
          if (!task.service_entry_id) {
            updates.service_entry_id = entryId;
          }
          if (Object.keys(updates).length > 1) {
            const { error: taskUpdateError } = await supabase
              .from('tasks')
              .update(updates)
              .eq('id', task.id);
            if (taskUpdateError) throw taskUpdateError;
          }
        }
      }

      if (suggestedResolvedRefs.length > 0) {
        for (const ref of suggestedResolvedRefs) {
          const { error: upsertError } = await supabase
            .from('hidden_service_tasks')
            .upsert(
              {
                user_id: user.id,
                car_id: selectedCarId,
                service_entry_id: ref.source_service_entry_id,
                task_text: ref.task_text,
                hidden: false,
                status: 'done',
              },
              { onConflict: 'user_id,service_entry_id,task_text' }
            );
          if (upsertError) throw upsertError;
        }
      }

      await supabase
        .from('service_entries')
        .update({
          tasks_resolved_ids: standaloneResolvedIds,
          service_tasks_resolved: suggestedResolvedRefs,
        })
        .eq('id', entryId);

      // 5) upload photos (optional)
      // Combine existing images (that weren't removed) with new uploads
      const existingMediaPaths = existingImages.map(img => img.path);
      const newMediaPaths: string[] = [];
      
      if (files.length > 0) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          for (const f of files) {
            const { uploadCarImage } = await import('@/lib/uploadCarImage');
            const path = await uploadCarImage({ file: f, userId: user.id, entityId: entryId, type: 'repair', accessToken: session.access_token });
            newMediaPaths.push(path);
          }
        }
      }
      
      // Update media array with existing + new images (only if there are changes)
      if (files.length > 0 || existingImages.length !== (loadedEntryData?.media?.length || 0)) {
        const allMediaPaths = [...existingMediaPaths, ...newMediaPaths];
        const media = allMediaPaths.map((p) => ({ path: p, type: 'image' }));
        await supabase.from('service_entries').update({ media }).eq('id', entryId);
      }

      // Show success dialog instead of navigating immediately
      setSuccessDialogOpen(true);
    } catch (err) {
      console.error('Error adding service entry:', err);
      setError(t('addService.error') || 'Failed to add service entry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedCarId) { setError(t('addService.error') || 'Failed to add service entry. Please try again.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      const { data: inserted, error: insertError } = await supabase
        .from('service_entries')
        .insert({
          car_id: selectedCarId,
          user_id: user.id,
          service_date: serviceDate.toISOString(),
          mechanic_name: mechanicName || null,
          shop_name: shopName || null,
          location: location || null,
          contact_phone: contactPhone || null,
          findings: finding || null,
          recommendations: recommendation || null,
          work_done: workDone.filter(w => w.title?.trim()),
          suggested_next_fixes: fixes.filter(f => f.fix?.trim() || f.due?.trim()),
          total_cents: cost ? Math.round(parseFloat(cost.replace(',', '.')) * 100) : null,
          status: 'draft',
          place_id: placeData?.place_id || null,
          lat: placeData?.lat || null,
          lng: placeData?.lng || null,
          service_types: selectedServiceTypes,
        })
        .select('id')
        .single();
      if (insertError) throw insertError;
      const entryId = inserted.id as string;

      if (odometerReading) {
        await supabase.from('odometer_readings').insert({
          car_id: selectedCarId,
          entered_by: user.id,
          reading: parseInt(odometerReading),
          recorded_at: serviceDate.toISOString(),
          source: 'service',
          related_id: entryId,
        });
      }

      // Sync fluid data to fluids table
      await syncFluidDataToFluidsTable(
        workDone.filter(w => w.title?.trim()),
        serviceDate,
        odometerReading,
        selectedCarId,
        user.id,
        entryId
      );

      if (files.length > 0) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          const paths: string[] = [];
          for (const f of files) {
            const { uploadCarImage } = await import('@/lib/uploadCarImage');
            const path = await uploadCarImage({ file: f, userId: user.id, entityId: entryId, type: 'repair', accessToken: session.access_token });
            paths.push(path);
          }
          const media = paths.map((p) => ({ path: p, type: 'image' }));
          await supabase.from('service_entries').update({ media }).eq('id', entryId);
        }
      }

      toast({ title: t('actions.save') || 'Saved', description: t('common.savedDraft') || 'Draft saved.' });
    } catch (e) {
      console.error('Draft save failed:', e);
      setError(t('addService.error') || 'Failed to add service entry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCar = cars.find(c => c.id === selectedCarId);
  const [openSection, setOpenSection] = useState<string | null>('basic');
  const basicCompleted = Boolean(selectedCarId) && Boolean(serviceDate);
  const workDoneCompleted = workDone.some(item => item.title.trim().length > 0);

  return (
    <>
      <PageLayout 
        title={t('addService.title') || 'Add Service Entry'} 
        showBackButton 
        onBack={() => navigate(-1)}
      >
        <div className="max-w-5xl mx-auto px-1 py-2">
          <form onSubmit={handleSubmit} className="pb-24" aria-describedby={error ? 'form-error' : undefined}>
            <div className="space-y-4">
              <Section
                title={t('addService.basic') || 'Basic Information'}
                isOpen={openSection === 'basic'}
                onToggle={() => setOpenSection(openSection === 'basic' ? null : 'basic')}
                rightNode={basicCompleted ? (
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full mr-1"
                    style={{ backgroundColor: '#e7fe52', boxShadow: '0px 6px 4px -2px #EEEEEE', border: '1px solid rgb(243, 244, 246)' }}
                    aria-label={t('addService.ariaLabels.completed') || 'Completed'}
                    title={t('addService.ariaLabels.completed') || 'Completed'}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6.5L5 8.5L9 3.5" stroke="#090909" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : null}
              >
              {/* Car Selection */}
              <div>
                <Label htmlFor="car" className="text-sm text-gray-600">{t('addService.car') || 'Car'} <span className="text-gray-600">*</span></Label>
                <Select value={selectedCarId} onValueChange={setSelectedCarId} required>
                  <SelectTrigger className="h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm">
                    <SelectValue placeholder={t('addService.selectCar') || 'Select a car'} />
                  </SelectTrigger>
                  <SelectContent>
                    {cars.map((car) => (
                      <SelectItem key={car.id} value={car.id}>
                        {car.nickname || `${car.brand} ${car.model}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date and Odometer on same row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col">
                  <div className="flex items-center gap-1 mb-1">
                    <Label htmlFor="serviceDate" className="text-sm text-gray-600">{t('common.date') || 'Date'} <span className="text-gray-600">*</span></Label>
                  </div>
                  <div className="relative">
                    <Input
                      id="serviceDate"
                      type="date"
                      value={(() => {
                        try {
                          return new Date(serviceDate).toISOString().slice(0,10);
                        } catch { return new Date().toISOString().slice(0,10); }
                      })()}
                      onChange={(e) => setServiceDate(new Date(e.target.value))}
                      className="h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm"
                      style={{
                        colorScheme: 'light',
                        color: 'transparent',
                        boxShadow: 'rgba(12, 12, 13, 0.05) 0px 4px 4px -1px'
                      }}
                      required
                    />
                    <div className="absolute inset-0 flex items-center px-3 pointer-events-none text-sm text-gray-700">
                      {(() => {
                        try {
                          return new Date(serviceDate).toLocaleDateString(language === 'lv' ? 'lv-LV' : 'en-US');
                        } catch {
                          return new Date().toLocaleDateString(language === 'lv' ? 'lv-LV' : 'en-US');
                        }
                      })()}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <Label htmlFor="odometer" className="text-sm text-gray-600">{t('addService.odometer') || 'Odometer (km)'}</Label>
                    <button
                      type="button"
                      onClick={() => setShowOdometerInfo(true)}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                    >
                      <Info className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <Input
                    id="odometer"
                    type="number"
                    min="0"
                    value={odometerReading}
                    onChange={(e) => setOdometerReading(e.target.value)}
                    placeholder={lastOdometerReading ? `${t('addService.lastOdo') || 'Last'}: ${lastOdometerReading.toLocaleString()} km` : (t('common.optional') || 'Optional')}
                    className="h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              </div>

              {/* Service Type - 4 equal buttons with icons */}
              <div>
                <Label className="text-sm text-gray-600 mb-2 block">{t('addService.type') || 'Service Type'}</Label>
                <div className="grid grid-cols-4 gap-2">
                  {SERVICE_TYPE_OPTIONS.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleServiceType(type)}
                      className={`w-full aspect-square rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition-colors shadow-sm hover:shadow ${
                        selectedServiceTypes.includes(type)
                          ? 'border-[#7c9ffa] bg-[#7c9ffa]/10'
                          : 'border-gray-300 bg-white hover:border-gray-400'
                      }`}
                    >
                      <img
                        src={`/icons/AddServiceEntry/${
                          type === 'repair' ? 'Repair' : 
                          type === 'maintenance' ? 'Maintanence' : 
                          type === 'upgrade' ? 'Upgrade' : 
                          'Inspection'
                        }.png`}
                        alt={t(`addService.serviceType.${type}`) || type}
                        className="w-12 h-12 object-contain"
                      />
                      <span className="text-[0.7rem] leading-tight text-gray-800">{t(`addService.serviceType.${type}`) || type}</span>
                    </button>
                  ))}
                </div>
              </div>

              </Section>

              <Section
                title={t('addService.mechanic') || 'Mechanic Details'}
                isOpen={openSection === 'mechanic'}
                onToggle={() => setOpenSection(openSection === 'mechanic' ? null : 'mechanic')}
                rightNode={providerSelected && (shopName || mechanicName || location || contactPhone) ? (
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full mr-1"
                    style={{ backgroundColor: '#e7fe52', boxShadow: '0px 6px 4px -2px #EEEEEE', border: '1px solid rgb(243, 244, 246)' }}
                    aria-label={t('addService.ariaLabels.completed') || 'Completed'}
                    title={t('addService.ariaLabels.completed') || 'Completed'}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6.5L5 8.5L9 3.5" stroke="#090909" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : undefined}
              >
                {/* Saved providers tiles - always visible */}
                <div className="flex justify-center gap-6">
                  {(() => { 
                    const hasShops = savedShops.length > 0 || providers.length > 0;
                    const chooseIcon = hasShops ? '/icons/AddServiceEntry/Choose.png' : '/icons/AddServiceEntry/Choose_disabled.png';
                    return (
                  <button
                    type="button"
                    onClick={() => hasShops && setProviderPickerOpen(true)}
                    disabled={!hasShops}
                    className={`h-24 aspect-square rounded-xl border flex flex-col items-center justify-center gap-1 transition-colors shadow-sm ${
                      hasShops
                        ? 'border-gray-300 bg-white hover:border-gray-400 hover:shadow'
                        : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <img src={chooseIcon} alt={t('addService.ariaLabels.chooseSavedShop') || 'Choose saved repair shop'} className="w-12 h-12 object-contain" />
                    <span className={`text-xs leading-tight ${hasShops ? 'text-gray-800' : 'text-gray-400'}`}>{t('actions.choose') || 'Select'}</span>
                  </button>
                  ); 
                  })()}
                  <button
                    type="button"
                    onClick={() => { 
                      setNewShopName(shopName || ''); 
                      setNewLocation(location || ''); 
                      setNewPhone(contactPhone || ''); 
                      setNewMechanicName(mechanicName || ''); 
                      setNewNotes('');
                      setNewRegistration('');
                      setNewBankAccounts(['']);
                      setEditingShopId(null);
                      setProviderAddOpen(true); 
                    }}
                    className={`h-24 aspect-square rounded-xl border flex flex-col items-center justify-center gap-1 transition-colors shadow-sm hover:shadow border-gray-300 bg-white hover:border-gray-400`}
                  >
                    <img src="/icons/AddServiceEntry/AddNew.png" alt={t('addService.ariaLabels.addNewShop') || 'Add new repair shop'} className="w-12 h-12 object-contain" />
                    <span className="text-xs leading-tight text-gray-800">{t('actions.add') || 'Add new'}</span>
                  </button>
                </div>

                {/* Repair Shop Card - shown after provider selection */}
                {providerSelected && (shopName || mechanicName || location || contactPhone) && (
                  <div className="mt-3">
                    <RepairShopCard
                      shopName={shopName}
                      mechanicName={mechanicName}
                      location={location}
                      contactPhone={contactPhone}
                      registrationNumber={newRegistration}
                      bankAccounts={newBankAccounts.filter(Boolean)}
                      notes={newNotes}
                      onClear={() => {
                        setShopName('');
                        setMechanicName('');
                        setLocation('');
                        setContactPhone('');
                        setNewRegistration('');
                        setNewBankAccounts(['']);
                        setNewNotes('');
                        setPlaceData(null);
                        setProviderSelected(false);
                        setSelectedSavedShopId('');
                      }}
                      onEdit={() => {
                        // Populate edit drawer with current form values
                        setNewShopName(shopName);
                        setNewLocation(location);
                        setNewPhone(contactPhone);
                        setNewMechanicName(mechanicName);
                        setNewNotes(newNotes);
                        setNewRegistration(newRegistration);
                        setNewBankAccounts(newBankAccounts.length > 0 ? newBankAccounts : ['']);
                        // If we have a selectedSavedShopId, use it for editing; otherwise null (new entry)
                        setEditingShopId(selectedSavedShopId || null);
                        setProviderAddOpen(true);
                      }}
                    />
                  </div>
                )}
              </Section>

              <Section
                title={t('addService.workDone') || 'Work Done'}
                isOpen={openSection === 'work'}
                onToggle={() => setOpenSection(openSection === 'work' ? null : 'work')}
                rightNode={workDoneCompleted ? (
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full mr-1"
                    style={{ backgroundColor: '#e7fe52', boxShadow: '0px 6px 4px -2px #EEEEEE', border: '1px solid rgb(243, 244, 246)' }}
                    aria-label={t('addService.ariaLabels.completed') || 'Completed'}
                    title={t('addService.ariaLabels.completed') || 'Completed'}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6.5L5 8.5L9 3.5" stroke="#090909" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : null}
              >
                {/* Service Request Owner Notes - Non-editable */}
                {serviceRequest && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900">
                        {t('serviceRequest.ownerNotes') || 'What I want done'}
                      </h4>
                      <a
                        href={`/service-request/view/${serviceRequest.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/service-request/view/${serviceRequest.id}`);
                        }}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {t('serviceRequest.viewServiceRequest') || 'View Service Request'}
                      </a>
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{serviceRequest.owner_notes}</p>
                  </div>
                )}
                {workDone.map((item, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <div className="flex-1 relative">
                      <WorkItemAutocomplete
                        value={item.title}
                        onChange={(value) => handleWorkDoneTitle(i, value)}
                        onSelect={(job) => handleWorkItemSelect(i, job)}
                        placeholder={`${t('addService.workItem') || 'Work item'} ${i + 1}`}
                        className="flex-1"
                      />
                      {item.title && (
                        <button
                          type="button"
                          onClick={() => clearWorkField(i)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label={t('actions.clear') || 'Clear'}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingWorkNoteIndex(i);
                        setWorkNoteDrawerOpen(true);
                      }}
                      className="flex-shrink-0 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                      aria-label={item.notes ? t('addService.editNote') || 'Edit note' : t('addService.addNote') || 'Add note'}
                    >
                      <img 
                        src={item.notes ? '/icons/AddServiceEntry/Commented.png' : '/icons/AddServiceEntry/Add comment.png'} 
                        alt={item.notes ? (t('addService.editNote') || 'Edit note') : (t('addService.addNote') || 'Add note')}
                        className="w-8 h-8"
                      />
                    </button>
                    {isRemoveMode && workDone.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeWorkField(i)}
                        className="flex-shrink-0 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label={t('actions.delete') || 'Delete'}
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
                {/* Add new work item button and Remove toggle */}
                <div className="flex items-center mt-8 mb-8 relative min-h-[2.5rem]">
                  {workDone.length >= 2 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (isRemoveMode) {
                          setIsRemoveMode(false);
                        } else {
                          setIsRemoveMode(true);
                        }
                      }}
                      className={`text-xs transition-colors ${
                        isRemoveMode
                          ? 'text-red-600 underline'
                          : 'text-gray-600 hover:text-gray-800 underline'
                      }`}
                    >
                      {t('actions.remove') || 'Remove'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={addWorkField}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full border-2 border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 flex items-center justify-center transition-colors"
                    aria-label={t('actions.add') || 'Add'}
                  >
                    <Plus className="w-5 h-5 text-gray-700" />
                  </button>
                </div>
                {/* Total Cost */}
                <div className="flex items-center gap-3 mt-4">
                  <Label htmlFor="cost" className="text-sm text-gray-600 whitespace-nowrap">{t('addService.totalCost') || 'Total Cost'}</Label>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{formatCurrency(0).replace(/[0-9.,\s]/g,'') || ''}</span>
                    <Input
                      id="cost"
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      value={cost}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Replace comma with dot for consistent decimal separator
                        value = value.replace(',', '.');
                        // Allow only numbers and one decimal point
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setCost(value);
                        }
                      }}
                      placeholder="0.00"
                      className="h-10 rounded-xl border border-gray-300 bg-white pl-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>
              </Section>

              <Section
                title={t('addService.findings') || 'Findings & Recommendations'}
                isOpen={openSection === 'findings'}
                onToggle={() => setOpenSection(openSection === 'findings' ? null : 'findings')}
                rightNode={(finding?.trim() || recommendation?.trim() || fixes.some(f => f.fix?.trim() || f.due?.trim())) ? (
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full mr-1"
                    style={{ backgroundColor: '#e7fe52', boxShadow: '0px 6px 4px -2px #EEEEEE', border: '1px solid rgb(243, 244, 246)' }}
                    aria-label={t('addService.ariaLabels.completed') || 'Completed'}
                    title={t('addService.ariaLabels.completed') || 'Completed'}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6.5L5 8.5L9 3.5" stroke="#090909" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : null}
              >
                {/* Findings Section */}
                <div className="mb-4">
                  <Label className="text-sm text-gray-600 mb-2 block">{t('addService.findingsTitle') || 'Findings'}</Label>
                  <Textarea
                    id="description"
                    value={finding}
                    onChange={(e) => setFinding(e.target.value)}
                    placeholder={t('addService.discoveredPh') || 'Describe findings'}
                    className="rounded-xl border border-gray-300 bg-white px-3 text-sm"
                    rows={3}
                  />
                </div>

                {/* Recommendations Section */}
                <div className="mb-4">
                  <Label className="text-sm text-gray-600 mb-2 block">{t('addService.recommendationsTitle') || 'Recommendations'}</Label>
                  <Textarea
                    id="recommendation"
                    value={recommendation}
                    onChange={(e) => setRecommendation(e.target.value)}
                    placeholder={t('addService.recommendationsPlaceholder') || 'Describe recommendations'}
                    className="rounded-xl border border-gray-300 bg-white px-3 text-sm"
                    rows={3}
                  />
                </div>

                {/* Suggested Next Fixes */}
                <div className="mt-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <Label className="text-sm text-gray-600">{t('addService.suggestedFixes') || 'Suggested Next Fixes'}</Label>
                    <span className="text-xs text-gray-600">{t('addService.setReminder') || 'Set reminder'}</span>
                  </div>
                </div>
                {fixes.map((row, i) => (
                  <div key={i} className="flex gap-2 items-center mb-2">
                    <div className="flex-1 relative">
                      <Input
                        placeholder={t('addService.fixWhat') || 'What fix is recommended?'}
                        value={row.fix}
                        readOnly
                        onClick={() => openFixDrawer(i)}
                        className="cursor-pointer"
                        style={{ fontSize: '0.875rem' }}
                      />
                      {row.fix && (
                        <button
                          type="button"
                          onClick={() => {
                            setFixes((prev) => {
                              const copy = [...prev];
                              copy[i].fix = '';
                              return copy;
                            });
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label={t('actions.clear') || 'Clear'}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => openIntervalDrawer(i)}
                      className="flex-shrink-0 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                      aria-label={row.due ? t('addService.editReminder') || 'Edit reminder' : t('addService.addReminder') || 'Add reminder'}
                    >
                      <img 
                        src={row.due ? '/icons/AddServiceEntry/Reminder.png' : '/icons/AddServiceEntry/Add reminder.png'} 
                        alt={row.due ? (t('addService.editReminder') || 'Edit reminder') : (t('addService.addReminder') || 'Add reminder')}
                        className="w-8 h-8"
                      />
                    </button>
                    {isRemoveFixMode && fixes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFixRow(i)}
                        className="flex-shrink-0 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label={t('actions.delete') || 'Delete'}
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
                <div className="flex items-center mt-8 mb-8 relative min-h-[2.5rem]">
                  {fixes.length >= 2 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (isRemoveFixMode) {
                          setIsRemoveFixMode(false);
                        } else {
                          setIsRemoveFixMode(true);
                        }
                      }}
                      className={`text-xs transition-colors ${
                        isRemoveFixMode
                          ? 'text-red-600 underline'
                          : 'text-gray-600 hover:text-gray-800 underline'
                      }`}
                    >
                      {t('actions.remove') || 'Remove'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={addFixRow}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full border-2 border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 flex items-center justify-center transition-colors"
                    aria-label={t('actions.add') || 'Add'}
                  >
                    <Plus className="w-5 h-5 text-gray-700" />
                  </button>
                </div>
              </Section>

              <Section
                title={t('addService.photos') || 'Photos'}
                isOpen={openSection === 'photos'}
                onToggle={() => setOpenSection(openSection === 'photos' ? null : 'photos')}
                rightNode={(existingImages.length > 0 || files.length > 0 || workDone.some(item => item.label_photo)) ? (
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full mr-1"
                    style={{ backgroundColor: '#e7fe52', boxShadow: '0px 6px 4px -2px #EEEEEE', border: '1px solid rgb(243, 244, 246)' }}
                    aria-label={t('addService.ariaLabels.completed') || 'Completed'}
                    title={t('addService.ariaLabels.completed') || 'Completed'}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6.5L5 8.5L9 3.5" stroke="#090909" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : null}
              >
                <div className="flex gap-3 flex-wrap justify-start">
                  {/* Show existing images from database */}
                  {existingImages.map((img, i) => (
                    <div key={`existing-${i}`} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img src={img.url} alt={t('addService.ariaLabels.photo') || 'Photo'} className="w-full h-full object-cover" />
                      <button type="button" className="absolute top-1 right-1 bg-black/70 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs" onClick={() => handleExistingImageRemove(img.path)}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {/* Show photos from files (main photos section) */}
                  {files.map((file, i) => (
                    <div key={`file-${i}`} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img src={URL.createObjectURL(file)} alt={t('addService.ariaLabels.photo') || 'Photo'} className="w-full h-full object-cover" />
                      <button type="button" className="absolute top-1 right-1 bg-black/70 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs" onClick={() => handleFileRemove(i)}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {/* Show label photos from work done items */}
                  {workDone.filter(item => item.label_photo).map((item, i) => (
                    <div key={`label-${i}`} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img src={item.label_photo} alt={t('addService.ariaLabels.labelPhoto') || 'Label photo'} className="w-full h-full object-cover" />
                      <div className="absolute bottom-1 left-1 bg-black/70 text-white text-[10px] px-1 rounded">
                        {item.title || 'Label'}
                      </div>
                    </div>
                  ))}
                  {existingImages.length + files.length + workDone.filter(item => item.label_photo).length < 5 && (
                    <label className="w-24 h-24 rounded-lg border border-dashed border-gray-300 text-gray-400 flex flex-col items-center justify-center text-sm cursor-pointer hover:bg-gray-50">
                      <Plus className="w-5 h-5" />
                      <span>{t('actions.add') || 'Add'}</span>
                      <input type="file" accept="image/*" multiple className="hidden" onChange={handleFileAdd} />
                    </label>
                  )}
                  {existingImages.length + files.length + workDone.filter(item => item.label_photo).length >= 5 && (
                    <div className="w-full text-xs text-gray-500 mt-2">
                      {t('addService.maxPhotos') || 'Max 5 photos'}
                    </div>
                  )}
                </div>
              </Section>

              <Section
                title={t('addService.resolvedIssues') || 'Resolved Issues'}
                isOpen={openSection === 'resolved'}
                onToggle={() => setOpenSection(openSection === 'resolved' ? null : 'resolved')}
                rightNode={(resolvedIssueIds.length > 0 || noneSelected) ? (
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full mr-1"
                    style={{ backgroundColor: '#e7fe52', boxShadow: '0px 6px 4px -2px #EEEEEE', border: '1px solid rgb(243, 244, 246)' }}
                    aria-label={t('addService.ariaLabels.completed') || 'Completed'}
                    title={t('addService.ariaLabels.completed') || 'Completed'}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6.5L5 8.5L9 3.5" stroke="#090909" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : null}
              >
                {openIssues.length > 0 ? (
                  <div className="space-y-2">
                    {openIssues.map((iss) => {
                      const isChecked = resolvedIssueIds.includes(iss.id);
                      return (
                        <div
                          key={iss.id}
                          className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
                        >
                          <label className="flex items-center cursor-pointer relative">
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNoneSelected(false);
                                  setResolvedIssueIds((prev) => [...prev, iss.id]);
                                } else {
                                  setResolvedIssueIds((prev) => prev.filter((id) => id !== iss.id));
                                }
                              }}
                            />
                            <div
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                isChecked
                                  ? 'border-[#7097fa] bg-[#7097fa]'
                                  : 'border-gray-300 bg-white'
                              }`}
                            >
                              {isChecked && (
                                <Check className="w-4 h-4 text-white" strokeWidth={3} />
                              )}
                            </div>
                          </label>
                          <div className="flex-1 flex items-center justify-between gap-2">
                            <span className={`text-sm ${isChecked ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                              {iss.title}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/symptom-summary/${selectedCarId}/${iss.id}`);
                              }}
                              className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-lg transition-colors"
                              aria-label={t('actions.view') || 'View issue'}
                            >
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {/* None of the recorded issues option */}
                    <div
                      className={`flex items-start gap-3 p-3 rounded-xl border border-gray-200 transition-colors ${
                        noneSelected ? 'bg-[#e7e7e7]' : 'hover:bg-gray-50'
                      }`}
                    >
                      <label className="flex items-center cursor-pointer relative">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={noneSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNoneSelected(true);
                              setResolvedIssueIds([]);
                            } else {
                              setNoneSelected(false);
                            }
                          }}
                        />
                        <div
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            noneSelected
                              ? 'border-[#7097fa] bg-[#7097fa]'
                              : 'border-gray-300 bg-white'
                          }`}
                        >
                          {noneSelected && (
                            <Check className="w-4 h-4 text-white" strokeWidth={3} />
                          )}
                        </div>
                      </label>
                      <div className="flex-1">
                        <span className={`text-sm ${noneSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                          {t('addService.noneOfRecordedIssues') || 'None of the recorded issues'}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {t('addService.noOpenIssues') || 'No open issues for this car'}
                  </p>
                )}
              </Section>

              <Section
                title={t('addService.resolvedTasks') || 'Resolved Tasks'}
                isOpen={openSection === 'resolvedTasks'}
                onToggle={() => setOpenSection(openSection === 'resolvedTasks' ? null : 'resolvedTasks')}
                rightNode={resolvedTaskIds.length > 0 ? (
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full mr-1"
                    style={{ backgroundColor: '#e7fe52', boxShadow: '0px 6px 4px -2px #EEEEEE', border: '1px solid rgb(243, 244, 246)' }}
                    aria-label={t('addService.ariaLabels.completed') || 'Completed'}
                    title={t('addService.ariaLabels.completed') || 'Completed'}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6.5L5 8.5L9 3.5" stroke="#090909" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : null}
              >
                {openTasks.length > 0 ? (
                  <div className="space-y-2">
                    {openTasks.map((task) => {
                      const isChecked = resolvedTaskIds.includes(task.id);
                      return (
                        <div
                          key={task.id}
                          className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
                        >
                          <label className="flex items-center cursor-pointer relative">
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setResolvedTaskIds((prev) => [...prev, task.id]);
                                } else {
                                  setResolvedTaskIds((prev) => prev.filter((id) => id !== task.id));
                                }
                              }}
                            />
                            <div
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                isChecked
                                  ? 'border-[#7097fa] bg-[#7097fa]'
                                  : 'border-gray-300 bg-white'
                              }`}
                            >
                              {isChecked && (
                                <Check className="w-4 h-4 text-white" strokeWidth={3} />
                              )}
                            </div>
                          </label>
                          <div className="flex-1 flex items-center justify-between gap-2">
                            <span className={`text-sm ${isChecked ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                              {task.title}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {t('addService.noOpenTasks') || 'No open tasks for this car'}
                  </p>
                )}
              </Section>

              {error && (
                <div id="form-error" className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>

            {/* Sticky footer actions */}
            <div className="fixed left-0 right-0 bottom-16 z-50 border-t border-gray-200 bg-white">
              <div className="mx-auto max-w-5xl px-1 py-1.5 flex gap-1.5">
                <Button type="button" variant="secondary" onClick={() => navigate(-1)} className="flex-1 rounded-xl border border-gray-300 text-xs py-2 h-9">{t('actions.cancel') || 'Cancel'}</Button>
                {!editEntryId && (
                  <Button type="button" variant="secondary" onClick={handleSaveDraft} disabled={submitting || !selectedCarId} className="flex-1 rounded-xl border border-gray-300 text-xs py-2 h-9">{t('actions.saveDraft') || 'Save Draft'}</Button>
                )}
                <Button type="submit" variant="default" disabled={submitting || !selectedCarId} className="flex-1 rounded-xl bg-[#7c9ffa] hover:bg-[#6b8ce8] text-xs py-2 h-9">
                  {submitting ? (
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 animate-spin" />
                      <span>{t('common.adding') || 'Adding...'}</span>
                    </div>
                  ) : (
                    t('actions.finish') || 'Finish'
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </PageLayout>

      {/* Odometer Info Dialog */}
      <Dialog open={showOdometerInfo} onOpenChange={setShowOdometerInfo}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Odometer Reading</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Odometer reading is helpful for fuel €/100km calculations
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Providers: Picker Drawer */}
      {providerPickerOpen && (
        <div className="fixed inset-0 bg-black/30 flex justify-center items-end z-50" role="dialog" aria-modal="true">
          <div className="bg-white w-full max-w-md rounded-t-2xl p-4 shadow-xl h-[70vh] flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-sm font-semibold text-gray-800">{t('addService.chooseProvider') || 'Choose repair shop'}</h2>
              <button onClick={() => setProviderPickerOpen(false)} className="text-gray-500 hover:text-gray-700" aria-label={t('actions.close') || 'Close'}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mb-2">
              <input
                value={providerQuery}
                onChange={(e) => setProviderQuery(e.target.value)}
                placeholder={t('common.search') || 'Search'}
                className="w-full h-9 rounded-xl border border-gray-300 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[#7c9ffa]/30"
              />
            </div>

            <div className="flex-1 overflow-auto rounded-xl border border-gray-200">
              {pickerList.map(p => {
                const isFromSupabase = savedShops.some(s => s.id === p.id);
                const normalizedPhone = Array.isArray(p.phones) && p.phones.length 
                  ? p.phones.map((ph: string) => ph.replace(/\s+/g, '')).join(', ')
                  : '';
                const displayAddress = p.formatted_address || '';
                const displayPhone = normalizedPhone || '';
                
                return (
                  <div 
                    key={p.id} 
                    className="border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                    onClick={(e) => {
                      // Only trigger if click wasn't on a button
                      const target = e.target as HTMLElement;
                      if (target.tagName !== 'BUTTON' && !target.closest('button')) {
                        handleUseShop(p);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{p.name || (t('addService.unnamed') || 'Unnamed')}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {[displayAddress, displayPhone].filter(Boolean).join(' • ') || (t('addService.noDetails') || 'No details')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 pb-2">
                      <button
                        type="button"
                        className="text-xs rounded-lg border border-red-300 px-3 py-1.5 bg-white hover:bg-red-50 text-red-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmShopId(p.id);
                        }}
                      >
                        {t('actions.delete') || 'Delete'}
                      </button>
                      <button
                        type="button"
                        className="text-xs rounded-lg border border-gray-300 px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditShop(p);
                        }}
                      >
                        {t('actions.edit') || 'Edit'}
                      </button>
                    </div>
                  </div>
                );
              })}
              {pickerList.length === 0 && (
                <div className="p-4 text-xs text-gray-500">{t('common.noData') || 'No saved shops yet.'}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Providers: Add/Edit Drawer */}
      {providerAddOpen && (
        <div className="fixed inset-0 bg-black/30 flex justify-center items-end z-50" role="dialog" aria-modal="true">
          <div className="bg-white w-full max-w-md rounded-t-2xl p-4 shadow-xl">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-sm font-semibold text-gray-800">
                {editingShopId ? (t('actions.edit') || 'Edit') + ' ' + (t('addService.repairShop') || 'repair shop') : (t('addService.saveProvider') || 'Save repair shop')}
              </h2>
              <button onClick={() => { setProviderAddOpen(false); setEditingShopId(null); }} className="text-gray-500 hover:text-gray-700" aria-label={t('actions.close') || 'Close'}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2">
              <div>
                <Label className="text-xs text-gray-600">{t('addService.location') || 'Location'}</Label>
                <div className="flex gap-2">
                  <Input value={newLocation} onChange={(e)=>setNewLocation(e.target.value)} className="h-9 rounded-xl border border-gray-300 px-3 text-sm flex-1" />
                  <button type="button" onClick={() => setProviderLocationPickerOpen(true)} className="h-9 px-3 rounded-xl border border-gray-300 text-xs bg-white hover:bg-gray-50">{t('actions.map') || 'Map'}</button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">{t('addService.shopName') || 'Repair Shop Name'} <span className="text-gray-600">*</span></Label>
                <Input value={newShopName} onChange={(e)=>setNewShopName(e.target.value)} className="h-9 rounded-xl border border-gray-300 px-3 text-sm" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">{t('addService.mechanicName') || 'Mechanic Name'}</Label>
                <Input value={newMechanicName} onChange={(e)=>setNewMechanicName(e.target.value)} className="h-9 rounded-xl border border-gray-300 px-3 text-sm" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">{t('addService.contactPhone') || 'Contact No'}</Label>
                <Input value={newPhone} onChange={(e)=>setNewPhone(e.target.value)} className="h-9 rounded-xl border border-gray-300 px-3 text-sm" />
              </div>
              <button
                type="button"
                onClick={() => setProviderMoreOpen(!providerMoreOpen)}
                className="text-xs text-gray-700 underline mt-1"
              >
                {t('common.additionalInfo') || 'Additional information'}
              </button>
              {providerMoreOpen && (
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs text-gray-600">{t('addService.registrationNumber') || 'Registration number'}</Label>
                    <Input value={newRegistration} onChange={(e)=>setNewRegistration(e.target.value)} className="h-9 rounded-xl border border-gray-300 px-3 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">{t('addService.bankAccounts') || 'Bank accounts'}</Label>
                    <div className="space-y-2">
                      {newBankAccounts.map((acc, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input value={acc} onChange={(e)=>{
                            const copy = [...newBankAccounts];
                            copy[idx] = e.target.value; setNewBankAccounts(copy);
                          }} className="h-9 rounded-xl border border-gray-300 px-3 text-sm flex-1" placeholder={t('addService.bankAccount') || 'Bank account'} />
                          <button type="button" onClick={()=> setNewBankAccounts(prev => prev.filter((_,i)=> i!==idx))} className="h-9 px-3 rounded-xl border border-gray-300 text-xs bg-white hover:bg-gray-50">{t('actions.delete') || 'Delete'}</button>
                        </div>
                      ))}
                      <button type="button" onClick={()=> setNewBankAccounts(prev => [...prev, ''])} className="text-xs text-gray-700 underline">{t('actions.add') || 'Add'} {t('addService.bankAccount') || 'Bank account'}</button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">{t('labels.notes') || 'Notes'}</Label>
                    <Textarea value={newNotes} onChange={(e)=>setNewNotes(e.target.value)} rows={3} className="rounded-xl border border-gray-300 px-3 text-sm" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between gap-2 pt-3">
              <button onClick={() => { setProviderAddOpen(false); setEditingShopId(null); }} className="text-sm text-gray-700 bg-gray-100 px-4 py-2 rounded-lg">{t('actions.cancel') || 'Cancel'}</button>
              <button
                onClick={async () => {
                  if (!newShopName.trim()) return;
                  
                  try {
                    const { data: { user } } = await supabase.auth.getUser();
                    if (!user?.id) return;

                    if (editingShopId) {
                      // Update existing shop
                      const isFromSupabase = savedShops.some(s => s.id === editingShopId);
                      
                      if (isFromSupabase) {
                        // Update in Supabase
                        await updateRepairShop(editingShopId, {
                          name: newShopName.trim() || null,
                          formatted_address: newLocation.trim() || null,
                          phones: newPhone.trim() ? [newPhone.trim()] : [],
                          mechanic_name: newMechanicName.trim() || null,
                          notes: newNotes.trim() || null,
                          registration_number: newRegistration.trim() || null,
                          bank_accounts: newBankAccounts.map(v => v.trim()).filter(Boolean).length > 0 
                            ? newBankAccounts.map(v => v.trim()).filter(Boolean) 
                            : null,
                        });
                        // Refresh list
                        if (selectedCarId) {
                          const shops = await getRepairShopsForCar(selectedCarId);
                          setSavedShops(shops);
                        }
                      } else {
                        // Update in localStorage
                        const provider = providers.find(p => p.id === editingShopId);
                        if (provider) {
                          removeProvider(editingShopId);
                          addProvider({
                            name: newShopName.trim(),
                            location: newLocation.trim() || undefined,
                            phone: newPhone.trim() || undefined,
                            mechanic: newMechanicName.trim() || undefined,
                            notes: newNotes.trim() || undefined,
                            registrationNumber: newRegistration.trim() || undefined,
                            bankAccounts: newBankAccounts.map(v => v.trim()).filter(Boolean)
                          });
                        }
                      }
                    } else {
                      // Create new shop
                      // Save to localStorage
                      addProvider({
                        name: newShopName.trim(),
                        location: newLocation.trim() || undefined,
                        phone: newPhone.trim() || undefined,
                        mechanic: newMechanicName.trim() || undefined,
                        notes: newNotes.trim() || undefined,
                        registrationNumber: newRegistration.trim() || undefined,
                        bankAccounts: newBankAccounts.map(v => v.trim()).filter(Boolean)
                      });
                      
                      // Also save to Supabase
                      const carIdToLink = selectedCarId || currentCar?.id || '';
                      if (!carIdToLink) {
                        throw new Error('No car selected');
                      }
                      await saveRepairShopForCar({
                        userId: user.id,
                        carId: carIdToLink,
                        shopData: {
                        name: newShopName.trim() || null,
                        formatted_address: newLocation.trim() || null,
                        phones: newPhone.trim() ? [newPhone.trim()] : [],
                        mechanic_name: newMechanicName.trim() || null,
                        notes: newNotes.trim() || null,
                        registration_number: newRegistration.trim() || null,
                        bank_accounts: newBankAccounts.map(v => v.trim()).filter(Boolean).length > 0 
                          ? newBankAccounts.map(v => v.trim()).filter(Boolean) 
                          : null,
                        place_id: placeData?.place_id || null,
                        lat: placeData?.lat || null,
                        lng: placeData?.lng || null,
                        website: (placeData as any)?.website || null,
                        },
                      });
                      
                      // Refresh saved shops list
                      if (selectedCarId) {
                        const shops = await getRepairShopsForCar(selectedCarId);
                        setSavedShops(shops);
                      }
                    }
                    
                    // Pre-fill the fields in the main form
                    setShopName(newShopName.trim());
                    setLocation(newLocation.trim());
                    setContactPhone(newPhone.trim());
                    setMechanicName(newMechanicName.trim());
                    setProviderSelected(true);
                    setProviderAddOpen(false);
                    setEditingShopId(null);
                    
                    toast({
                      title: editingShopId ? (t('common.updated') || 'Updated') : (t('actions.save') || 'Saved'),
                      description: editingShopId 
                        ? (t('common.updatedSuccess') || 'Repair shop updated successfully.')
                        : (t('common.savedSuccess') || 'Repair shop saved successfully.'),
                    });
                  } catch (e: any) {
                    console.error('Failed to save repair shop:', e);
                    toast({
                      title: t('common.error') || 'Error',
                      description: t('common.saveError') || 'Failed to save repair shop.',
                      variant: 'destructive',
                    });
                  }
                }}
                disabled={!newShopName.trim()}
                className="text-sm text-gray-900 bg-[#e7fe52] px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingShopId ? (t('actions.save') || 'Save') : (t('actions.save') || 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmShopId && (
        <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50" role="dialog" aria-modal="true">
          <div className="bg-white w-full max-w-sm rounded-2xl p-6 shadow-xl mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('common.confirmDelete') || 'Confirm Delete'}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {t('common.deleteConfirmMessage') || 'Are you sure you want to delete this repair shop? This action cannot be undone.'}
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setDeleteConfirmShopId(null)}
                className="flex-1 rounded-xl"
              >
                {t('actions.cancel') || 'Cancel'}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDeleteShop(deleteConfirmShopId)}
                className="flex-1 rounded-xl"
              >
                {t('actions.delete') || 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Drawers for Findings & Recommendations */}
      {fixDrawerOpen && (
        <div className="fixed inset-0 bg-black/30 flex justify-center items-end z-50" role="dialog" aria-modal="true">
          <div className="bg-white w-full max-w-md rounded-t-2xl p-4 shadow-xl h-1/2 flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-sm font-semibold text-gray-800">{t('addService.addRecommendedFix') || 'Add Recommended Fix'}</h2>
              <button onClick={() => setFixDrawerOpen(false)} className="text-gray-500 hover:text-gray-700" aria-label={t('actions.close') || 'Close'}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">{t('addService.fixHelper') || 'Describe what fix is recommended and why.'}</p>
            <div className="flex-1 min-h-0">
              <Textarea placeholder={t('addService.startTyping') || 'Start typing...'} value={activeIndex==null? '' : fixes[activeIndex]?.fix || ''} onChange={(e) => handleFixAddDirect(e.target.value)} />
            </div>
            <div className="flex justify-between gap-2 pt-3">
              <button onClick={() => setFixDrawerOpen(false)} className="text-sm text-gray-700 bg-gray-100 px-4 py-2 rounded-lg">{t('actions.cancel') || 'Cancel'}</button>
              <button onClick={confirmFixAdd} className="text-sm text-gray-900 bg-[#e7fe52] px-4 py-2 rounded-lg font-medium">{t('actions.add') || 'Add'}</button>
            </div>
          </div>
        </div>
      )}

      {intervalDrawerOpen && (
        <div className="fixed inset-0 bg-black/30 flex justify-center items-end z-50" role="dialog" aria-modal="true">
          <div className="bg-white w-full max-w-md rounded-t-2xl p-4 shadow-xl">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-sm font-semibold text-gray-800">{t('addService.setFixInterval') || 'Set Next Fix Interval'}</h2>
              <button onClick={() => setIntervalDrawerOpen(false)} className="text-gray-500 hover:text-gray-700" aria-label={t('actions.close') || 'Close'}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">{t('addService.intervalHelper') || 'Choose how to measure the next recommended service — by kilometers or time.'}</p>
            <div className="flex rounded-full border border-gray-200 bg-gray-100 text-xs font-medium h-[32px] mb-3">
              {(['km','time'] as const).map((opt) => (
                <button key={opt} type="button" onClick={() => setMode(opt)} className={`flex-1 px-3 rounded-full transition ${mode === opt ? 'bg-[#e7fe52] text-gray-900' : 'text-gray-500'}`}>
                  {opt === 'km' ? 'KM' : (t('addService.time') || 'Time')}
                </button>
              ))}
            </div>
            {mode === 'km' ? (
              <select className="w-full h-[32px] mb-4 rounded-xl border border-gray-200 bg-[#fafafa] px-2 text-sm focus:ring-2 focus:ring-[#7097fa]/30 focus:border-[#7097fa]/40" value={km} onChange={(e) => setKm(e.target.value)}>
                <option value="">{t('addService.selectKm') || 'Select KM Interval'}</option>
                {Array.from({ length: 10 }, (_, i) => (i + 1) * 5000).map((v) => (
                  <option key={v} value={v}>{t('addService.inKm', { value: v }) || `In ${v} km`}</option>
                ))}
              </select>
            ) : (
              <select className="w-full h-[32px] mb-4 rounded-xl border border-gray-200 bg-[#fafafa] px-2 text-sm focus:ring-2 focus:ring-[#7097fa]/30 focus:border-[#7097fa]/40" value={time} onChange={(e) => setTime(e.target.value)}>
                <option value="">{t('addService.selectTime') || 'Select Time Option'}</option>
                <option>{t('addService.nextVisit') || 'Next visit'}</option>
                <option>{t('addService.nextYear') || 'Next year'}</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m}>{t('addService.inMonths', { value: m }) || `In ${m} month(s)`}</option>
                ))}
              </select>
            )}
            <div className="flex justify-between gap-2 pt-1">
              <button onClick={() => setIntervalDrawerOpen(false)} className="text-sm text-gray-700 bg-gray-100 px-4 py-2 rounded-lg">{t('actions.cancel') || 'Cancel'}</button>
              <button onClick={handleIntervalAdd} className="text-sm text-gray-900 bg-[#e7fe52] px-4 py-2 rounded-lg font-medium">{t('actions.add') || 'Add'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Work Note Drawer */}
      {workNoteDrawerOpen && editingWorkNoteIndex !== null && (() => {
        const currentItem = workDone[editingWorkNoteIndex];
        const needsFluidDetails = currentItem ? requiresFluidDetails(currentItem.title) : false;
        const needsColor = currentItem ? isCoolantWork(currentItem.title) : false;

        const handleLabelPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setLabelPhotoFile(file);
            const previewUrl = URL.createObjectURL(file);
            setLabelPhotoPreview(previewUrl);
          }
        };

        const handleRemoveLabelPhoto = () => {
          if (labelPhotoPreview && labelPhotoPreview.startsWith('blob:')) {
            URL.revokeObjectURL(labelPhotoPreview);
          }
          setLabelPhotoFile(null);
          setLabelPhotoPreview(null);
          handleWorkDoneLabelPhoto(editingWorkNoteIndex, '');
          if (labelPhotoInputRef.current) {
            labelPhotoInputRef.current.value = '';
          }
        };

        return (
          <div className="fixed inset-0 bg-black/30 flex justify-center items-end z-50" role="dialog" aria-modal="true">
            <div className="bg-white w-full max-w-md rounded-t-2xl p-4 shadow-xl max-h-[90vh] flex flex-col overflow-y-auto">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-sm font-semibold text-gray-800">
                  {currentItem?.notes 
                    ? (t('addService.editNote') || 'Edit Note')
                    : (t('addService.addNote') || 'Add Note')}
                </h2>
                <button 
                  onClick={() => {
                    setWorkNoteDrawerOpen(false);
                    setEditingWorkNoteIndex(null);
                  }} 
                  className="text-gray-500 hover:text-gray-700" 
                  aria-label={t('actions.close') || 'Close'}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                {t('addService.workItem') || 'Work item'}: {currentItem?.title || `${t('addService.workItem') || 'Work item'} ${editingWorkNoteIndex + 1}`}
              </p>
              
              <div className="space-y-4 mb-4">
                {/* Notes */}
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">{t('addService.notes') || 'Notes'}</Label>
                  <Textarea 
                    placeholder={t('addService.notes') || 'Notes'} 
                    value={currentItem?.notes ?? ''} 
                    onChange={(e) => handleWorkDoneNotes(editingWorkNoteIndex, e.target.value)}
                    className="w-full resize-none text-sm"
                    rows={4}
                  />
                </div>

                {/* Fluid Details (only for specific work items) */}
                {needsFluidDetails && (
                  <>
                    {/* Specification/Code */}
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">
                        {t('addService.specCode') || 'Specification/Code'} <span className="text-gray-400">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder={t('addService.specCodePlaceholder') || 'e.g., 5W-30, DOT 4'}
                        value={currentItem?.spec_code ?? ''}
                        onChange={(e) => handleWorkDoneSpecCode(editingWorkNoteIndex, e.target.value)}
                        className="text-sm"
                      />
                    </div>

                    {/* Label Photo */}
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">
                        {t('addService.labelPhoto') || 'Label Photo'}
                      </Label>
                      {labelPhotoPreview ? (
                        <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200">
                          <img src={labelPhotoPreview} alt="Label preview" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={handleRemoveLabelPhoto}
                            className="absolute top-1 right-1 bg-black/70 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="block w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors">
                          <Plus className="w-6 h-6 text-gray-400" />
                          <input
                            ref={labelPhotoInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleLabelPhotoChange}
                          />
                        </label>
                      )}
                    </div>

                    {/* Color (only for coolant) */}
                    {needsColor && (
                      <div>
                        <Label className="text-xs text-gray-600 mb-1 block">
                          {t('addService.color') || 'Color'}
                        </Label>
                        <Input
                          type="text"
                          placeholder={t('addService.colorPlaceholder') || 'e.g., Red, Green, Blue'}
                          value={currentItem?.color ?? ''}
                          onChange={(e) => handleWorkDoneColor(editingWorkNoteIndex, e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex justify-between gap-2 pt-3 border-t border-gray-100">
                <button 
                  onClick={() => {
                    setWorkNoteDrawerOpen(false);
                    setEditingWorkNoteIndex(null);
                  }} 
                  className="text-sm text-gray-700 bg-gray-100 px-4 py-2 rounded-lg"
                >
                  {t('actions.cancel') || 'Cancel'}
                </button>
                <button 
                  onClick={async () => {
                    // Upload label photo if a new file was selected
                    if (labelPhotoFile && selectedCarId) {
                      try {
                        const { data: { user } } = await supabase.auth.getUser();
                        if (!user) return;
                        
                        const { data: { session } } = await supabase.auth.getSession();
                        if (!session) return;

                        const { uploadCarImage } = await import('@/lib/uploadCarImage');
                        const path = await uploadCarImage({ 
                          file: labelPhotoFile, 
                          userId: user.id, 
                          entityId: selectedCarId, 
                          type: 'repair', 
                          accessToken: session.access_token 
                        });
                        handleWorkDoneLabelPhoto(editingWorkNoteIndex, path);
                      } catch (error) {
                        console.error('Failed to upload label photo:', error);
                      }
                    }
                    setWorkNoteDrawerOpen(false);
                    setEditingWorkNoteIndex(null);
                  }} 
                  className="text-sm text-gray-900 bg-[#e7fe52] px-4 py-2 rounded-lg font-medium"
                >
                  {t('actions.save') || 'Save'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Location Picker */}
      <LocationPicker
        isOpen={providerLocationPickerOpen}
        onClose={() => setProviderLocationPickerOpen(false)}
        onSelect={async (place: PlaceData) => {
          console.log('[DEBUG] ========== AddServiceEntryPage onSelect CALLED ==========');
          console.log('[DEBUG] Received place data:', JSON.stringify(place, null, 2));

          // Determine if this is a business (has website or phones) vs just an address
          const isBusiness = !!(place.website || (place.phones && place.phones.length > 0));
          
          // Clear selectedSavedShopId since we're selecting a new place (not from saved shops)
          setSelectedSavedShopId('');
          
          // 1) Fill visible inputs immediately (no awaits) - fill EVERY field
          // Only set shop name if it's a business (has website or phones)
          // Address-only selections should not populate shop name
          setShopName(isBusiness ? (place.name ?? '') : '');
          setLocation(place.formatted_address ?? '');
          // Normalize phones array: join with commas, remove spaces
          const normalizedPhone = place.phones?.length 
            ? place.phones.map(p => p.replace(/\s+/g, '')).join(', ')
            : '';
          setContactPhone(normalizedPhone);
          
          // 2) Set placeData with ALL place info (place_id, lat, lng, website) immediately
          setPlaceData({
            place_id: place.place_id,
            lat: place.lat,
            lng: place.lng,
            website: place.website ?? null,
          } as any);

          // 3) Keep drawer fields in sync
          // Only set shop name in drawer if it's a business
          setNewShopName(isBusiness ? (place.name ?? '') : '');
          setNewLocation(place.formatted_address ?? '');
          setNewPhone(normalizedPhone);

          // 4) Show the editable block
          setProviderSelected(true);

          // Note: Save to Supabase only happens when user explicitly clicks "Save" in the drawer
          // No automatic save here to avoid double-saving

          // 5) Focus next field after drawer closes
          // Use double requestAnimationFrame to ensure drawer animation completes
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const mechanicInput = document.getElementById('mechanicName');
              if (mechanicInput) {
                (mechanicInput as HTMLElement).focus();
              }
            });
          });
        }}
        language={language}
      />

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
        <DialogContent className="sm:max-w-[360px] p-6">
          <div className="text-center">
            <img src="/icons/Success.png" alt={t('addService.ariaLabels.success') || 'Success'} className="w-12 h-12 mx-auto mb-3" />
            <DialogTitle className="text-lg font-semibold mb-2">
              {t('addService.successTitle') || 'Service Entry Saved'}
            </DialogTitle>
            <p className="text-sm text-gray-600 mb-6">
              {t('addService.successDesc') || 'Your service entry has been successfully recorded.'}
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => {
                  setSuccessDialogOpen(false);
                  navigate(`/records/${selectedCarId}?tab=repairs`);
                }}
                className="w-full bg-[#e7fe52] text-black hover:bg-[#e7fe52]/90"
              >
                {t('addService.viewRepairs') || 'View Repairs'}
              </Button>
              <Button
                onClick={() => {
                  setSuccessDialogOpen(false);
                  navigate('/');
                }}
                variant="secondary"
                className="w-full"
              >
                {t('actions.home') || 'Home'}
              </Button>
              <Button
                onClick={() => {
                  setSuccessDialogOpen(false);
                  navigate(-1);
                }}
                variant="secondary"
                className="w-full"
              >
                {t('actions.close') || 'Close'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddServiceEntryPage;
